import { useEffect, useCallback, useRef } from 'react';

// ============================================================================
// Hook Props & Return Types
// ============================================================================

export interface UseDialogProps {
  open: boolean;
  onClose: () => void;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  dialogRef: React.RefObject<HTMLElement | null>;
}

export interface UseDialogReturn {
  handleOverlayClick: (e: React.MouseEvent) => void;
}

// ============================================================================
// Focusable elements selector
// ============================================================================

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// ============================================================================
// useDialog Hook
// ============================================================================

export const useDialog = ({
  open,
  onClose,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  dialogRef,
}: UseDialogProps): UseDialogReturn => {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Save previously focused element; move focus into dialog on open, restore on close
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;

      const raf = requestAnimationFrame(() => {
        if (
          dialogRef.current &&
          !dialogRef.current.contains(document.activeElement)
        ) {
          dialogRef.current.focus();
        }
      });

      return () => cancelAnimationFrame(raf);
    } else {
      previousFocusRef.current?.focus();
      return undefined;
    }
  }, [open, dialogRef]);

  // Handle keyboard events: Escape + focus trap
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (closeOnEscape) {
          onClose();
        }
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
        ).filter((el) => !el.closest('[aria-hidden="true"]'));

        if (focusable.length === 0) {
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) {
          return;
        }

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [closeOnEscape, onClose, dialogRef]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeOnOverlayClick && e.target === e.currentTarget) {
        onClose();
      }
    },
    [closeOnOverlayClick, onClose]
  );

  return { handleOverlayClick };
};
